// =============================================================================
// io/parsers/carryIn.ts — closing a position that was opened before this file.
// -----------------------------------------------------------------------------
// 🚨 THE DEFECT THIS EXISTS FOR, measured (#12):
//
// A file whose first fill for an instrument CLOSES something opened earlier has
// nothing to match against, so every fills parser opens a position on the
// opposite side instead. That phantom then eats the next real opening fill,
// which therefore cannot open, and every trade after it lands one leg out of
// step. Run through the real import path in ATAS format: a −$207.50 long came
// out as a −$1,577.50 short, and **all three self-checks stayed silent** — they
// cannot see it, because the phantom makes the parser's own arithmetic balance.
//
// ▶ THE ANSWER IS NOT A CLEVERER GUESS. It is that the app already computed the
// position — account, contract, side, quantity, average entry, open time — on
// the PREVIOUS import, and threw it away. This module consumes it instead.
//
// ⚠ PURE, and the prior positions are passed IN. No database, no clock, no
// state. The store that supplies them is a separate concern, and keeping it out
// means the matching rules can be tested against real files without one.
// =============================================================================

/**
 * A position carried in from an earlier import, with the quantity still
 * available to close against.
 *
 * ⚠ DECLARED HERE RATHER THAN EXTENDING `OpenPosition`, so this module imports
 * nothing from `sierra.ts` and `sierra.ts` can import from it. A type-only
 * cycle would compile; a runtime one is the kind of fragility that bites at
 * module-init order, and there is no reason to risk it. `OpenPosition` is
 * structurally assignable to this, which is all the callers need.
 */
export interface PriorPosition {
  account: string;
  /** The catalogue root — `MNQ`. */
  contract: string;
  /** The symbol as written, normalised — may or may not carry a month. */
  contractFull: string | null;
  side: 'long' | 'short';
  openQty: number;
  avgEntry: number;
  openTime: string;
  /** Stable identity, and the key `remaining` is tracked under. */
  fingerprint: string;
  /**
   * Commission already paid to OPEN this position, still unattached to any
   * trade.
   *
   * 🚨 IT IS REAL MONEY AND IT IS OTHERWISE LOST. The opening fill's fee was in
   * the previous file; because that fill never became a trade, nothing has ever
   * counted it. Dropping it here would report a cross-session trade at half its
   * true cost — and this project has already shipped one import path that
   * counted every fee-less file at zero cost (D160).
   */
  openFee: number;
}

/** How much of a prior position a fill consumed, and at what entry. */
export interface ConsumedLeg {
  position: PriorPosition;
  qty: number;
  /** Pro-rata share of the position's unattached opening commission. */
  openFee: number;
}

const MONTH_TAIL = /[FGHJKMNQUVXZ]\d{1,2}$/;

/**
 * Normalise a symbol far enough to compare it across sources.
 *
 * 🔴 THE EXCHANGE IS WRITTEN TWO WAYS AND THE SHARED HELPER ONLY STRIPS ONE.
 * `stripSymbolDecorations` removes a HYPHENATED exchange (`-CME`) and leaves a
 * DOTTED one, and Sierra writes the dotted form — so the identical instrument
 * reaches here as `MNQU6.CME` from Sierra and `MNQU6` from ATAS. A match that
 * compared those raw would simply never fire across sources, and would fail
 * SILENTLY, which is the failure mode this whole area keeps producing.
 *
 * ⚠ Normalising HERE rather than changing `stripSymbolDecorations` is
 * deliberate: that function's output is written to every trade row as
 * `contractFull`, so changing its shape splits stored rows from new ones and is
 * its own decision. This is a comparison key, and nothing persists it.
 */
export function normaliseSymbol(raw: string | null | undefined): string {
  return String(raw ?? '')
    .toUpperCase()
    .trim()
    .replace(/^\[[^\]]*\]\s*/, '')
    .replace(/[-.](CME|CBOT|NYMEX|COMEX|GLOBEX|ICE|ICEUS|NYBOT|EUREX|CFE|DME|SGX)$/, '')
    .trim();
}

/** Does this symbol state a delivery month, or is it just a root? */
export function hasMonth(raw: string | null | undefined): boolean {
  return MONTH_TAIL.test(normaliseSymbol(raw));
}

/**
 * Can a fill for `b` close a position held in `a`?
 *
 * 🚨 THREE CASES, AND THE THIRD IS THE ONE THAT MATTERS:
 *
 *   both state a month  → they must be the SAME month. `MNQU6` cannot close
 *                         `MNQZ6`; they are different instruments and pairing
 *                         them moves the trade by the whole calendar spread,
 *                         20–40 points on ES near a roll. That is the Databento
 *                         `*.C.0` bug and #25 arriving a third way.
 *   neither states one  → compare the roots. A platform that never writes months
 *                         gives us nothing better, and there is no month here to
 *                         get wrong.
 *   one states, one not → 🔴 NO MATCH. "The export did not say" is not "the
 *                         front month". Refusing is the safe direction: the
 *                         fill still lands, the position stays open, and the
 *                         import preview says a close could not be matched.
 *                         Guessing would fabricate a priced trade.
 */
export function canClose(
  a: { contract: string; contractFull: string | null },
  b: { contract: string; contractFull: string | null },
): boolean {
  const am = hasMonth(a.contractFull);
  const bm = hasMonth(b.contractFull);
  if (am !== bm) return false;
  if (am) return normaliseSymbol(a.contractFull) === normaliseSymbol(b.contractFull);
  return normaliseSymbol(a.contract) === normaliseSymbol(b.contract)
    && normaliseSymbol(a.contract).length > 0;
}

/**
 * Consume prior positions that this fill closes.
 *
 * Mutates `remaining` — the caller keeps one map for the whole walk so a second
 * fill cannot re-consume what the first already used.
 *
 * ⚠ FIFO BY OPEN TIME, matching how every queue in this codebase pairs.
 * ⚠ Only positions opened STRICTLY BEFORE this fill. A position opened later in
 * the same session is not something this fill can have closed, and allowing it
 * would let a stale row eat a genuine new trade.
 */
export function consumePrior(
  prior: readonly PriorPosition[],
  remaining: Map<string, number>,
  fill: {
    account: string;
    contract: string;
    contractFull: string | null;
    /** The side this fill WOULD open — so it closes the opposite. */
    side: 'long' | 'short';
    qty: number;
    time: string;
  },
): ConsumedLeg[] {
  const wanted: 'long' | 'short' = fill.side === 'long' ? 'short' : 'long';
  const eligible = prior
    .filter((p) => p.side === wanted)
    .filter((p) => normaliseSymbol(p.account) === normaliseSymbol(fill.account))
    .filter((p) => canClose(p, fill))
    .filter((p) => !p.openTime || !fill.time || p.openTime < fill.time)
    .filter((p) => (remaining.get(p.fingerprint) ?? p.openQty) > 0)
    .sort((x, y) => String(x.openTime).localeCompare(String(y.openTime)));

  const out: ConsumedLeg[] = [];
  let left = fill.qty;
  for (const p of eligible) {
    if (left <= 0) break;
    const avail = remaining.get(p.fingerprint) ?? p.openQty;
    const take = Math.min(avail, left);
    if (take <= 0) continue;
    remaining.set(p.fingerprint, avail - take);
    // Pro-rata: half the position closed carries half its unattached open fee.
    const feeShare = p.openQty > 0 ? (p.openFee || 0) * (take / p.openQty) : 0;
    out.push({ position: p, qty: take, openFee: feeShare });
    left -= take;
  }
  return out;
}
